import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef } from 'react';
import services from '../data/services.jsx';
import { Helmet } from 'react-helmet-async';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <>
      <Helmet>
        <title>Services | Nayrix Enterprise Solutions</title>
        <meta name="description" content="Explore Nayrix's range of enterprise software, SaaS, API, cloud, analytics, and security solutions." />
        <meta property="og:title" content="Services | Nayrix Enterprise Solutions" />
        <meta property="og:description" content="Explore Nayrix's range of enterprise software, SaaS, API, cloud, analytics, and security solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nayrix.com/services" />
        <link rel="canonical" href="https://nayrix.com/services" />
      </Helmet>
      <main className="relative">
        {/* Hero/Intro Section */}
        <section className="relative py-24 bg-gradient-to-br from-primary-50 via-light-bg to-primary-100 dark:from-dark-bg dark:via-dark-bg/80 dark:to-dark-bg/60 animate-gradient-move">
          <div className="container px-4 mx-auto text-center max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold mb-6 dark:text-dark-text text-light-text"
            >
              Enterprise Solutions
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl dark:text-dark-text-secondary text-light-text-secondary mb-8"
            >
              Comprehensive software solutions designed to transform your business operations and drive growth.
            </motion.p>
          </div>
        </section>
        {/* Services Grid */}
        <section className="py-20 bg-light-bg dark:bg-dark-bg/50">
          <div className="container px-4 mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 dark:text-dark-text text-light-text">
                Our Services
              </h2>
              <p className="text-lg dark:text-dark-text-secondary text-light-text-secondary max-w-2xl mx-auto">
                Explore our range of premium enterprise solutions, each crafted for performance, security, and scalability.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {services.map((service, idx) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="relative p-8 rounded-2xl bg-white/60 dark:bg-dark-bg/60 shadow-xl dark:shadow-2xl border dark:border-dark-border border-light-border backdrop-blur-md transition-all duration-300 cursor-pointer group hover:scale-[1.025] hover:shadow-2xl"
                  onClick={() => setSelectedService(service)}
                  tabIndex={0}
                  role="button"
                  aria-label={`Learn more about ${service.title}`}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedService(service); }}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="p-4 rounded-full dark:bg-dark-accent/10 bg-light-accent/10 mb-6 dark:text-dark-accent text-light-accent transition-colors duration-300">
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-semibold mb-2 dark:text-dark-text text-light-text">
                      {service.title}
                    </h3>
                    <p className="dark:text-dark-text-secondary text-light-text-secondary mb-4">
                      {service.description}
                    </p>
                    <span className="inline-block mt-2 text-primary-600 dark:text-primary-400 font-medium underline-offset-4 hover:underline transition-colors duration-200">
                      Learn More
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
            {/* Modal Popup for Service Details */}
            <AnimatePresence>
              {selectedService && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
                  onClick={() => setSelectedService(null)}
                  aria-modal="true"
                  role="dialog"
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="bg-light-bg dark:bg-dark-bg rounded-2xl shadow-2xl max-w-lg w-full p-8 relative border dark:border-dark-border border-light-border"
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => setSelectedService(null)}
                      className="absolute top-4 right-4 text-xl dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none"
                      aria-label="Close service details"
                    >
                      &times;
                    </button>
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-4">{selectedService.icon}</div>
                      <h3 className="text-2xl font-bold mb-2 dark:text-dark-text text-light-text">{selectedService.title}</h3>
                      <p className="mb-4 dark:text-dark-text-secondary text-light-text-secondary">{selectedService.description}</p>
                      {selectedService.details && (
                        <div className="text-left w-full mt-4">
                          {selectedService.details.features && (
                            <div className="mb-3">
                              <h4 className="font-semibold mb-1 dark:text-dark-text text-light-text">Key Features:</h4>
                              <ul className="list-disc list-inside text-sm dark:text-dark-text-secondary text-light-text-secondary">
                                {selectedService.details.features.map((f, i) => <li key={i}>{f}</li>)}
                              </ul>
                            </div>
                          )}
                          {selectedService.details.benefits && (
                            <div className="mb-3">
                              <h4 className="font-semibold mb-1 dark:text-dark-text text-light-text">Benefits:</h4>
                              <ul className="list-disc list-inside text-sm dark:text-dark-text-secondary text-light-text-secondary">
                                {selectedService.details.benefits.map((b, i) => <li key={i}>{b}</li>)}
                              </ul>
                            </div>
                          )}
                          {selectedService.details.technologies && (
                            <div>
                              <h4 className="font-semibold mb-1 dark:text-dark-text text-light-text">Technologies:</h4>
                              <ul className="list-disc list-inside text-sm dark:text-dark-text-secondary text-light-text-secondary">
                                {selectedService.details.technologies.map((t, i) => <li key={i}>{t}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="text-center mt-16">
              <a href="/contact" className="inline-block text-primary-600 dark:text-primary-400 font-medium underline-offset-4 hover:underline transition-colors duration-200">Contact us</a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Services;
